import { Box, Chip } from '@mui/material'
import React, { isValidElement, type ReactNode, useEffect, useState } from 'react'
import { StyledText } from '../styles'
import { UploadCsvButton } from '../components/UploadCsvButton'
import { getLocallyStoredDescriptionsByPage, getLocallyStoredTaskIdsByPage, getLocallyStoredUploadStatusesByPage, setLocallyStoredTaskIdsByPage, setLocallyStoredUploadStatusesAndDescriptionsByPage, updatePendingDescription, useLoadingDots, useTaskStatusQuery } from '../helper'
import { UploadCsvTable } from '../components/UploadCsvTable'
import { facultyTooltipText, studentsTooltipText, coursesTooltipText } from '../components/TooltipText'
import { wizardKeys } from '../constants'

const options = ['faculty', 'students', 'courses']

export const defaultUploadStatus = <Chip label='Not Started' color='primary' variant='outlined' size='small'/>

export const UploadPeopleAndCoursesPage: React.FC = (): JSX.Element => {
  const loadingDotCount = useLoadingDots()
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, ReactNode>>(getLocallyStoredUploadStatusesByPage(wizardKeys.UploadPeopleAndCoursesPage, options))
  const [taskIds, setTaskIds] = useState<Record<string, string>>(getLocallyStoredTaskIdsByPage(wizardKeys.UploadPeopleAndCoursesPage, options))
  const [descriptions, setDescriptions] = useState<Record<string, ReactNode>>(getLocallyStoredDescriptionsByPage(wizardKeys.UploadPeopleAndCoursesPage, taskIds, loadingDotCount, options))

  // locally store taskIds when updated
  useEffect(() => { setLocallyStoredTaskIdsByPage(wizardKeys.UploadPeopleAndCoursesPage, taskIds) }, [taskIds])

  // locally store upload statuses and descriptions when updated
  useEffect(() => { setLocallyStoredUploadStatusesAndDescriptionsByPage(wizardKeys.UploadPeopleAndCoursesPage, uploadStatuses) }, [uploadStatuses])

  // updates the pending description whenever the status changes
  useEffect(() => { updatePendingDescription(uploadStatuses, setDescriptions, loadingDotCount, options) }, [loadingDotCount, uploadStatuses])

  // fetches status of tasks from BE
  const { data: unusedData } = useTaskStatusQuery(taskIds, uploadStatuses, setUploadStatuses, setDescriptions, loadingDotCount)

  // logic for the rows in the table
  const commonProps = { setUploadStatuses, setDescriptions, setTaskIds, loadingDotCount }
  const rows = [
    { id: 1, step: 'Faculty and Staff', upload: <UploadCsvButton value='faculty' {...commonProps} />, status: uploadStatuses.faculty, details: descriptions.faculty, tooltipText: facultyTooltipText },
    { id: 2, step: 'Students', upload: <UploadCsvButton value='students' {...commonProps} />, status: uploadStatuses.students, details: descriptions.students, tooltipText: studentsTooltipText },
    { id: 3, step: 'Courses', upload: <UploadCsvButton value='courses' {...commonProps} />, status: uploadStatuses.courses, details: descriptions.courses, tooltipText: coursesTooltipText }
  ]
  return (
    <Box
      flexGrow='1'
      display='flex'
      flexDirection='column'
      alignItems='flex-start'
      justifyContent='center'
    >
      <StyledText textAlign='start'>
        Please provide the following information for your term. If you have already uploaded this information, you may skip these steps.
      </StyledText>
      <UploadCsvTable rows={rows} />
      <StyledText textAlign='start'>
        This process may take a long time. You can close this window and come back later to check the status of your uploads.
      </StyledText>
    </Box>
  )
}
